import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert,
  Stack,
  Checkbox,
  Link,
} from "@mui/material";
import {ChangeEvent, useContext, useEffect, useState} from "react";
import { GlobalContext } from "context/Provider";
import API from "api";
import Session from "helpers/session";
import {CRACK_COOKIE_TOKEN_TYPES} from "../../../../helpers/appConstants";
import {
  readingsForDisplay,
  getUnitsFor,
  normalizeThresholds,
  validateCheckedThresholds
} from "./GlobalThresholds.utility";
import React from "react";
import ThresholdLine from "views/PatientBoard/microViews/History/Thresholds/ThresholdLine";
import { updateThreshold, setThresholds, removeThresholds } from "../../../PatientBoard/microViews/History/Thresholds/Thresholds.utility";
import {
  StyledTableHead,
  StyledTableRow,
  StyledRowTitle,
  StyledSubmitButton,
  GlobalThreshContainer,
  TableContainerWrapper,
  StyledTableFooter
} from "./GlobalThresholds.styles";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export default function GlobalThresholds() {
  const [thresholdData, setThresholdData] = useState<any[]>([]);
  const [showSuccess, setSuccess] = useState<string>("");
  const [lowCheckedMap, setLowCheckedMap] = useState<Record<string, boolean>>({});
  const [highCheckedMap, setHighCheckedMap] = useState<Record<string, boolean>>({});
  const [applyDisabled, setApply] = useState<boolean>(true);
  const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});
  const { coordinator } = CRACK_COOKIE_TOKEN_TYPES;
  const { threshold, thresholdDispatch, provider, config } = useContext(GlobalContext);

  useEffect(() => {
    API.kore.readings
      .getThreshold(
        "default",
        undefined,
        Session.crackCookieToken(coordinator)
      )
      .then((res) => {
        if (res) {
          setThresholdData(res);
          const normalized = normalizeThresholds(res);
          thresholdDispatch({
            type: "SET_ALL_THRESHOLDS",
            payload: normalized
          });

          const lowInit: Record<string, boolean> = {};
          const highInit: Record<string, boolean> = {};
          readingsForDisplay.forEach((reading) => {
            reading.subTypes.forEach((subType) => {
              lowInit[subType.key] = Boolean(normalized.moderate?.[subType.key]);
              highInit[subType.key] = Boolean(normalized.high?.[subType.key]);
            });
          });

          setLowCheckedMap(lowInit);
          setHighCheckedMap(highInit);
        }
      })
      .catch((err) => {
        throw err;
      });
  }, []);

  useEffect(() => {
    if (config?.globalThreshOptions) {
      setVisibleMap({ ...config.globalThreshOptions });
    }
  }, [config]);

  const submitThresholds = async () => {
    const promiseArray: Promise<any>[] = [];
    const breachTypes: ("moderate" | "high")[] = ["moderate", "high"];

    if (!threshold) return;

    if (!validateCheckedThresholds(threshold, readingsForDisplay, lowCheckedMap, highCheckedMap)) {
      setApply(true);
      setSuccess("incomplete");
      setTimeout(() => setSuccess(""), 6000);
      return;
    }

    for (const reading of readingsForDisplay) {
      const readingKey = reading.readingKey;
      const subTypes = reading.subTypes;

      for (const breach of breachTypes) {
        const checkedKeys =
          breach === "moderate"
            ? subTypes.map(st => st.key).filter(key => lowCheckedMap[key])
            : subTypes.map(st => st.key).filter(key => highCheckedMap[key]);

        const uncheckedKeys =
          breach === "moderate"
            ? subTypes.map(st => st.key).filter(key => !lowCheckedMap[key])
            : subTypes.map(st => st.key).filter(key => !highCheckedMap[key]);

        const existing = thresholdData.find(
          threshold => threshold.readingType === readingKey && threshold.breachType === breach
        );

        if (subTypes.length === 1 && existing) {
          const key = subTypes[0].key;
          const isChecked = breach === "moderate" ? lowCheckedMap[key] : highCheckedMap[key];
          if (!isChecked && existing?.value?.[key]) {
            promiseArray.push(
              removeThresholds(existing).then(() => {
                setThresholdData(prev => prev.filter(t => t._id !== existing._id));
                thresholdDispatch({
                  type: "REMOVE_THRESHOLD",
                  payload: { breach, key }
                });
              })
            );
            continue;
          }
        }

        if (subTypes.length > 1 && existing) {
          if (checkedKeys.length === 0) {
            const existingKeys = Object.keys(existing.value);
            promiseArray.push(
              removeThresholds(existing).then(() => {
                setThresholdData(prev => prev.filter(t => t._id !== existing._id));
                existingKeys.forEach(key => {
                  thresholdDispatch({
                    type: "REMOVE_THRESHOLD",
                    payload: { breach, key }
                  });
                });
              })
            );
            continue;
          }

          if (uncheckedKeys.length > 0) {
            const updatedValue = { ...existing.value };
            uncheckedKeys.forEach(key => {
              delete updatedValue[key];
              thresholdDispatch({
                type: "REMOVE_THRESHOLD",
                payload: { breach, key }
              });
            });

            promiseArray.push(
              updateThreshold({ ...existing, value: updatedValue, _id: existing._id })
            );
          }
        }

        const value: Record<string, any> = {};
        checkedKeys.forEach(key => {
          if ((threshold[breach] as any)[key]) {
            value[key] = (threshold[breach] as any)[key];
          }
        });

        if (Object.keys(value).length === 0) continue;

        const payload = {
          memberId: "default",
          coordinator: provider?.loggedInClient.id,
          breachType: breach,
          readingType: readingKey,
          value
        };

        if (existing?._id) {
          promiseArray.push(updateThreshold({ ...payload, _id: existing._id }));
        } else {
          promiseArray.push(
            setThresholds(payload).then(newThreshold => {
              setThresholdData(prev => [...prev, newThreshold]);
            })
          );
        }
      }
    }

    try {
      await Promise.all(promiseArray);
      setSuccess("success");
      setTimeout(() => setSuccess(""), 6000);
      setApply(true);
    } catch (err) {
      setSuccess("fail");
      setTimeout(() => setSuccess(""), 6000);
    }
  };

  const toggleLow = (key: string) => {
    setApply(false);
    setLowCheckedMap((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const toggleHigh = (key: string) => {
    setApply(false);
    setHighCheckedMap((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleChange = (event: ChangeEvent<HTMLElement>) => {
    setSuccess("");
    setApply(false);
  };

  const isSubTypeVisible = (key: string) => {
    return visibleMap[key];
  };
  const isReadingTypeVisible = (reading: any) => {
    return reading.subTypes.some((st: any) => isSubTypeVisible(st.key));
  };

  return (
    <GlobalThreshContainer>
      <TableContainerWrapper>
        <Box>
          <h2>Global Thresholds</h2>
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }}>
            <StyledTableHead>
              <TableRow>
                <TableCell align={"left"} colSpan={1} sx={{ fontSize: '1.1rem' }}>
                  Reading Type
                </TableCell>
                <TableCell align={"left"} colSpan={1} sx={{ fontSize: '1.1rem' }}>
                  Moderate Threshold
                </TableCell>
                <TableCell align={"left"} colSpan={1}sx={{ fontSize: '1.1rem' }}>
                  High Threshold
                </TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {readingsForDisplay.map((reading) => {
                if (!isReadingTypeVisible(reading)) return null;
                return (
                  <React.Fragment key={reading.readingKey}>
                    <StyledTableRow $isParent={true}>
                      <TableCell>
                        <div id={`reading-${reading.readingKey}`} style={{ scrollMarginTop: "20px" }}>
                          <StyledRowTitle $isParent={true}>{reading.label}</StyledRowTitle>
                        </div>
                      </TableCell>
                      <TableCell>
                        <WarningAmberIcon sx={{ color: "#A67A00", pl: 1 }} />
                      </TableCell>
                      <TableCell>
                        <ReportGmailerrorredIcon sx={{ color: "red", pl: 1 }} />
                      </TableCell>
                    </StyledTableRow>
                    {reading.subTypes.map((subType) => {
                      if (!isSubTypeVisible(subType.key)) return null;
                      return (
                        <StyledTableRow $isParent={false}>
                          <TableCell>
                            <StyledRowTitle $isParent={false}>
                              {subType.label}
                            </StyledRowTitle>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row">
                              <Checkbox
                                checked={lowCheckedMap[subType.key] ?? false}
                                onChange={(event) => {
                                  toggleLow(subType.key);
                                  handleChange(event);
                                }}
                              />
                              <ThresholdLine
                                type={subType.key}
                                alertLevel="moderate"
                                units={getUnitsFor(subType.key)}
                                isGlobal={true}
                                isDisabled={!lowCheckedMap[subType.key]}
                                onChangeFunc={(e: any) => {
                                  handleChange(e);
                                }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row">
                              <Checkbox
                                checked={highCheckedMap[subType.key] ?? false}
                                onChange={(event) => {
                                  toggleHigh(subType.key);
                                  handleChange(event);
                                }}
                              />
                              <ThresholdLine
                                type={subType.key}
                                alertLevel="high"
                                units={getUnitsFor(subType.key)}
                                isGlobal={true}
                                isDisabled={!highCheckedMap[subType.key]}
                                onChangeFunc={(e: any) => {
                                  handleChange(e);
                                }}
                              />
                            </Stack>
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <StyledTableFooter>
          <Box width="70%">
            {(showSuccess === "success" || showSuccess === "fail" || showSuccess === "incomplete") && (
              <Alert
                severity={
                  showSuccess === "success"
                    ? "success"
                    : showSuccess === "fail"
                      ? "error"
                      : "warning"
                }
              >
                {showSuccess === "success"
                  ? "Your configuration changes have been saved."
                  : showSuccess === "fail"
                    ? "Failed to save thresholds. Please try again."
                    : "Please ensure all checked fields have both low and high values set."}
              </Alert>
            )}
          </Box>
          <Box ml="auto">
            <StyledSubmitButton onClick={submitThresholds} isDisabled={applyDisabled}>
              Submit Global Thresholds
            </StyledSubmitButton>
          </Box>
        </StyledTableFooter>
      </TableContainerWrapper>
      <Box width="200px" sx={{ paddingLeft: "5px", paddingRight: "5px" }}>
        <h4>Jump to Section</h4>
        <Stack spacing={2}>
          {readingsForDisplay.map((reading) => {
            if (!isReadingTypeVisible(reading)) return null;
            return (
              <Link
                key={reading.readingKey}
                href="#"
                underline="hover"
                sx={{cursor: "pointer"}}
                onClick={(event) => {
                  event.preventDefault();
                  const element = document.getElementById(`reading-${reading.readingKey}`);
                  if (element) {
                    element.scrollIntoView({behavior: "smooth", block: "start"});
                  }
                }}
              >
                {reading.label}
              </Link>
            );
          })}
        </Stack>
      </Box>
    </GlobalThreshContainer>
  );
}